import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type DocTreeNode, type DocVersion } from '../api';
import { Page, Card, Button, ButtonLink, Select, Textarea, Field, EmptyState, Markdown } from '../ui';
import { MarkdownEditor, type MdSelection } from '../ui/MarkdownEditor';
import { DocChat } from '../ui/DocChat';
import { DiffView } from '../ui/Code';
import { PromptModal } from '../ui/PromptModal';
import { parseDocBlocks, type ProposedDoc } from '../lib/docBlocks';
import { readSSE } from '../lib/sse';

function iconFor(name: string): string {
  if (/^index\.md$/i.test(name)) return '🗂️';
  if (/\.html?$/i.test(name)) return '🌐';
  return '📄';
}

export function DocsBrowser() {
  const { slug } = useParams<{ slug: string }>();
  const [tree, setTree] = useState<DocTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // seleção: arquivo aberto OU pasta selecionada (pra ações).
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedDir, setSelectedDir] = useState<string>('docs');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [editDiff, setEditDiff] = useState<string | null>(null); // diff da última edição feita pela IA
  const [promptModal, setPromptModal] = useState<
    { title: string; label?: string; initial?: string; placeholder?: string; confirmLabel?: string; onConfirm: (v: string) => void } | null
  >(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['docs']));
  const [sourcesMode, setSourcesMode] = useState(false); // base vem de fontes vinculadas (raízes = fontes)
  const [filter, setFilter] = useState(''); // busca de arquivos no explorador

  // Publicação da base no repo de código (branch base). Salvar é local + colaborativo;
  // publicar (push em lote) roda sozinho ~10 min após a última edição, ou por este botão.
  const [pubStatus, setPubStatus] = useState<Awaited<ReturnType<typeof api.docsPublishStatus>> | null>(null);
  const [publishing, setPublishing] = useState(false);

  // Histórico de versões do arquivo aberto (git): quem/quando + ver/reverter.
  const [history, setHistory] = useState<DocVersion[] | null>(null);
  const [histBusy, setHistBusy] = useState(false);
  const [viewingSha, setViewingSha] = useState<string | null>(null);
  // Trecho selecionado no editor (qualquer modo) → foco do chat de IA.
  const [docSel, setDocSel] = useState<MdSelection | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dirInputRef = useRef<HTMLInputElement>(null);

  // Busca: lista plana de todos os arquivos (pra filtrar quando há termo de busca).
  const filteredFiles = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return null;
    const out: { name: string; path: string }[] = [];
    const walk = (ns: DocTreeNode[]) => {
      for (const n of ns) {
        if (n.type === 'file') out.push({ name: n.name, path: n.path });
        else if (n.children) walk(n.children);
      }
    };
    walk(tree);
    return out.filter((f) => f.path.toLowerCase().includes(q));
  }, [filter, tree]);

  // Onboarding: "Analisar projeto" → progresso ao vivo (SSE) + docs propostos.
  const [analyzing, setAnalyzing] = useState(false);
  const [proposed, setProposed] = useState<ProposedDoc[]>([]);
  const [analyzeErr, setAnalyzeErr] = useState<string | null>(null);
  const [progress, setProgress] = useState<string[]>([]);
  const [narration, setNarration] = useState('');
  const [showAnalyzeForm, setShowAnalyzeForm] = useState(false);
  const [analyzeInstructions, setAnalyzeInstructions] = useState('');
  const [analyzeBranch, setAnalyzeBranch] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const rawRef = useRef('');

  // Carrega as branches (do origin) quando o formulário de análise abre.
  useEffect(() => {
    if (showAnalyzeForm && slug && branches.length === 0) {
      api.codeBranches(slug).then((r) => setBranches(r.branches)).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnalyzeForm, slug]);

  async function analyze() {
    if (!slug || analyzing) return;
    setShowAnalyzeForm(false);
    setAnalyzing(true);
    setAnalyzeErr(null);
    setProposed([]);
    setProgress([]);
    setNarration('');
    rawRef.current = '';
    const pushProg = (line: string) => setProgress((p) => [...p.slice(-50), line]);
    try {
      const res = await fetch(`/v1/projects/${encodeURIComponent(slug)}/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          instructions: analyzeInstructions.trim() || undefined,
          ref: analyzeBranch.trim() || undefined,
        }),
      });
      if (!res.ok || !res.body) throw new Error(`http ${res.status}`);
      let finalText = '';
      let gotError: string | null = null;
      await readSSE(res.body, (event, data) => {
        if (event === 'status') pushProg('• ' + String(data['message'] ?? ''));
        else if (event === 'tool') pushProg('→ ' + String(data['info'] ?? data['name'] ?? ''));
        else if (event === 'text') {
          rawRef.current += String(data['delta'] ?? '');
          setNarration(parseDocBlocks(rawRef.current).stripped);
        } else if (event === 'result') finalText = String(data['text'] ?? '');
        else if (event === 'error') gotError = String(data['message'] ?? 'erro');
      });
      if (!finalText) finalText = rawRef.current;
      const { docs } = parseDocBlocks(finalText);
      setProposed(docs);
      if (docs.length === 0) {
        setAnalyzeErr(gotError ? `A análise falhou: ${gotError}` : 'A análise não retornou documentos. Tente de novo, ou converse com o agente Onboarding no chat.');
      }
    } catch (e) {
      setAnalyzeErr(`Falha na análise (${String((e as Error)?.message ?? e)}). Tente de novo.`);
    } finally {
      setAnalyzing(false);
    }
  }

  async function saveProposed(doc: ProposedDoc) {
    if (!slug) return;
    try {
      await api.saveDoc(slug, doc.path, doc.content);
      setProposed((p) => p.filter((d) => d.path !== doc.path));
      reload();
    } catch {
      setErr('Falha ao salvar documento proposto');
    }
  }

  function reload(keepFile?: string | null) {
    if (!slug) return;
    setLoading(true);
    api
      .getDocsTree(slug)
      .then(({ tree, mode: m }) => {
        setTree(tree);
        const srcMode = m === 'sources';
        setSourcesMode(srcMode);
        if (srcMode) {
          // Fontes: as raízes são as próprias fontes. Expande todas e seleciona a 1ª.
          setExpanded(new Set(tree.map((n) => n.path)));
          setSelectedDir((cur) => (cur && tree.some((n) => n.path === cur) ? cur : tree[0]?.path ?? ''));
        }
        if (keepFile) openFile(keepFile);
      })
      .catch(() => setErr('Falha ao carregar documentos'))
      .finally(() => setLoading(false));
  }

  function reloadPubStatus() {
    if (!slug) return;
    api.docsPublishStatus(slug).then(setPubStatus).catch(() => undefined);
  }

  async function publishNow() {
    if (!slug || publishing) return;
    setPublishing(true);
    setMsg(null);
    try {
      const r = await api.publishDocs(slug);
      if (r.published) setMsg(`Publicado no repo (${r.files ?? '?'} arquivo(s), ${r.sha?.slice(0, 8) ?? ''}).`);
      else setMsg(r.reason === 'sem mudanças' ? 'Nada novo para publicar.' : `Não publicado: ${r.reason ?? 'motivo desconhecido'}.`);
    } catch {
      setErr('Falha ao publicar no repo.');
    } finally {
      setPublishing(false);
      reloadPubStatus();
    }
  }

  useEffect(() => {
    reload();
    reloadPubStatus();
    if (slug) api.getProject(slug).then((r) => setProjectId(r.project.id)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  function openFile(path: string) {
    if (!slug) return;
    setSelectedFile(path);
    setSelectedDir(parentDir(path));
    setHistory(null);          // fecha histórico do arquivo anterior
    setViewingSha(null);
    setEditDiff(null);
    api
      .getDoc(slug, path)
      .then(({ content }) => setContent(content))
      .catch(() => setContent('*(não foi possível carregar)*'));
  }

  // Salva o conteúdo (chamado pelo editor inline ao sair de edição).
  async function saveContent(v: string) {
    if (!slug || !selectedFile) return;
    setContent(v);
    setEditDiff(null);
    try {
      await api.saveDoc(slug, selectedFile, v);
      setMsg('Salvo.');
    } catch {
      setErr('Falha ao salvar');
    }
  }

  // Após um turno do chat embutido: recarrega o doc, mostra o diff do que a IA mudou.
  async function onAfterTurn(shaBefore: string | null) {
    if (!slug || !selectedFile) return;
    try {
      const { content: c } = await api.getDoc(slug, selectedFile);
      setContent(c);
    } catch { /* ignore */ }
    if (shaBefore) {
      try {
        const { diff } = await api.docDiff(slug, selectedFile, shaBefore);
        setEditDiff(diff && diff.trim() ? diff : null);
      } catch { setEditDiff(null); }
    }
    api.getDocsTree(slug).then(({ tree }) => setTree(tree)).catch(() => undefined);
  }

  async function openHistory() {
    if (!slug || !selectedFile) return;
    setHistBusy(true);
    setErr(null);
    try {
      const { versions } = await api.docHistory(slug, selectedFile);
      setHistory(versions);
    } catch {
      setErr('Falha ao carregar histórico');
    } finally {
      setHistBusy(false);
    }
  }

  async function viewVersion(sha: string) {
    if (!slug || !selectedFile) return;
    try {
      const { content: c } = await api.docAt(slug, selectedFile, sha);
      setContent(c);
      setViewingSha(sha);
    } catch {
      setErr('Falha ao carregar a versão');
    }
  }

  function backToCurrent() {
    setViewingSha(null);
    if (selectedFile) openFile(selectedFile);
  }

  async function revertVersion(sha: string) {
    if (!slug || !selectedFile || busy) return;
    if (!confirm(`Reverter "${selectedFile}" para a versão ${sha.slice(0, 8)}? Isso cria um novo commit (auditável; dá pra reverter de novo).`)) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.docRevert(slug, selectedFile, sha);
      setContent(r.content);
      setViewingSha(null);
      setMsg('Documento revertido.');
      await openHistory();
      reload(selectedFile);
    } catch {
      setErr('Falha ao reverter');
    } finally {
      setBusy(false);
    }
  }

  function renameDoc() {
    if (!slug || !selectedFile || busy) return;
    const cur = selectedFile.replace(/^docs\//, '');
    setPromptModal({
      title: 'Renomear / mover',
      label: 'Novo caminho/nome (relativo a docs/)',
      initial: cur,
      placeholder: 'ex: marca/tom-de-voz.md',
      confirmLabel: 'Mover',
      onConfirm: async (proposed) => {
        setPromptModal(null);
        if (!selectedFile || proposed.trim() === cur) return;
        setBusy(true);
        setErr(null);
        try {
          const { path } = await api.moveDoc(slug, selectedFile, proposed.trim());
          setMsg('Arquivo movido/renomeado.');
          reload(path);
        } catch {
          setErr('Falha ao mover/renomear (o destino já existe?)');
        } finally {
          setBusy(false);
        }
      },
    });
  }

  function newFile() {
    if (!slug) return;
    setPromptModal({
      title: 'Novo arquivo',
      label: `Dentro de ${selectedDir}/`,
      placeholder: 'ex: tom-de-voz.md',
      confirmLabel: 'Criar',
      onConfirm: async (name) => {
        setPromptModal(null);
        const fname = name.endsWith('.md') ? name : `${name}.md`;
        const path = `${selectedDir}/${fname}`;
        try {
          await api.saveDoc(slug, path, `# ${fname.replace(/\.md$/, '')}\n\n`);
          reload(path);
        } catch {
          setErr('Falha ao criar arquivo');
        }
      },
    });
  }

  function newFolder() {
    if (!slug) return;
    setPromptModal({
      title: 'Nova pasta',
      label: `Dentro de ${selectedDir}/`,
      placeholder: 'ex: arquitetura',
      confirmLabel: 'Criar pasta',
      onConfirm: async (name) => {
        setPromptModal(null);
        const path = `${selectedDir}/${name}`;
        try {
          await api.createDocFolder(slug, path);
          setExpanded((s) => new Set(s).add(path));
          reload();
        } catch {
          setErr('Falha ao criar pasta');
        }
      },
    });
  }

  async function removeSelected(path: string, isDir: boolean) {
    if (!slug) return;
    if (!confirm(`Apagar ${isDir ? 'a pasta' : 'o arquivo'} "${path}"?${isDir ? ' Todo o conteúdo será removido.' : ''}`)) return;
    try {
      await api.deleteDoc(slug, path);
      if (selectedFile === path || (isDir && selectedFile?.startsWith(path + '/'))) {
        setSelectedFile(null);
        setContent('');
      }
      reload();
    } catch {
      setErr('Falha ao apagar');
    }
  }

  async function generateIndex(dirPath: string) {
    if (!slug || busy) return;
    setBusy(true);
    setMsg('A IA está montando o índice…');
    setErr(null);
    try {
      const { path } = await api.generateDocIndex(slug, dirPath);
      setMsg('Índice gerado.');
      reload(path);
    } catch {
      setErr('Falha ao gerar índice');
      setMsg(null);
    } finally {
      setBusy(false);
    }
  }

  async function onImport(files: FileList | null) {
    if (!slug || !files || files.length === 0) return;
    setBusy(true);
    setErr(null);
    setMsg(`Importando ${files.length} arquivo(s)…`);
    try {
      const res = await api.importDocs(slug, selectedDir, Array.from(files));
      setMsg(`Importados ${res.written}.${res.skipped.length ? ` Ignorados ${res.skipped.length} (não-texto).` : ''}`);
      reload();
    } catch {
      setErr('Falha no import');
      setMsg(null);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (dirInputRef.current) dirInputRef.current.value = '';
    }
  }

  const isEmpty = useMemo(() => tree.length === 0, [tree]);

  return (
    <Page
      title="Documentos"
      subtitle="Base de conhecimento do projeto — pastas, arquivos e índices. Versionada em git; agentes leem daqui."
      actions={
        <>
          <Button variant="primary" onClick={() => setShowAnalyzeForm((s) => !s)} disabled={analyzing}>{analyzing ? 'analisando…' : '✨ Analisar projeto'}</Button>
          {pubStatus?.repo && (
            <Button
              variant="secondary"
              onClick={publishNow}
              disabled={publishing}
              title={`Publica a base no repo de código (${pubStatus.baseBranch}). Salvar já guarda aqui; publicar leva pro git pra o dev clonar.${pubStatus.last ? ` Última publicação: ${new Date(pubStatus.last.at).toLocaleString('pt-BR')}.` : ''}`}
            >
              {publishing ? 'publicando…' : `⇧ Publicar no projeto${pubStatus.pending ? ' •' : ''}`}
            </Button>
          )}
          <Button variant="ghost" onClick={() => window.open(`/wiki/${slug}`, '_blank', 'noopener')}>📖 Abrir como wiki</Button>
          <ButtonLink to="/account/sync-app" variant="ghost" title="App de desktop que sincroniza estes docs com uma pasta local">⬇ App de sync</ButtonLink>
          <Button variant="ghost" onClick={newFolder} disabled={busy}>+ Pasta</Button>
          <Button variant="ghost" onClick={newFile} disabled={busy}>+ Arquivo</Button>
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={busy}>Importar arquivos</Button>
          <Button variant="secondary" onClick={() => dirInputRef.current?.click()} disabled={busy}>Importar pasta</Button>
        </>
      }
    >
      <input ref={fileInputRef} type="file" accept=".md,.markdown,.txt,.html,.htm" multiple hidden onChange={(e) => onImport(e.target.files)} />
      {/* webkitdirectory: import de pasta inteira preservando subpastas */}
      <input
        ref={dirInputRef}
        type="file"
        hidden
        onChange={(e) => onImport(e.target.files)}
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
      />

      {err && <div className="chat-error">{err}</div>}
      {msg && <div className="agent-editor-msg">{msg}</div>}

      {showAnalyzeForm && !analyzing && (
        <Card style={{ marginBottom: 16 }}>
          <div className="card-title-row">
            <strong>Analisar projeto</strong>
            <span className="users-muted">o agente lê o repositório e propõe documentos pra base</span>
          </div>
          <Field label="O que focar / verificar (opcional)" hint="mensagem livre pro agente — ex: 'verifique a segurança do login e os webhooks', 'documente o fluxo de pagamento', 'foca no backend'.">
            <Textarea rows={3} value={analyzeInstructions} onChange={(e) => setAnalyzeInstructions(e.target.value)} placeholder="Direcione a análise…" />
          </Field>
          <Field label="Branch (opcional)" hint="vazio = branch padrão do repo">
            <Select value={analyzeBranch} onChange={(e) => setAnalyzeBranch(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">padrão do repo</option>
              {branches.map((b) => <option key={b} value={b}>{b}</option>)}
            </Select>
          </Field>
          <div className="docs-analyze-foot">
            <Button variant="primary" onClick={analyze}>🚀 Rodar análise</Button>
            <Link className="docs-analyze-chat" to={`/p/${slug}/chat?agent=onboarding`}>💬 Conversar com o agente (a qualquer momento)</Link>
          </div>
        </Card>
      )}
      {analyzing && (
        <Card style={{ marginBottom: 16 }}>
          <div className="card-title-row">
            <strong>✨ Analisando o projeto…</strong>
            <span className="users-muted">o agente está lendo o repositório (pode levar alguns minutos)</span>
          </div>
          {narration && <div className="analyze-narration">{narration}</div>}
          {progress.length > 0 && (
            <div className="analyze-progress">
              {progress.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          )}
        </Card>
      )}
      {analyzeErr && <div className="chat-error">{analyzeErr}</div>}
      {proposed.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div className="card-title-row">
            <strong>Documentos propostos ({proposed.length})</strong>
            <span className="users-muted">revise e salve os que fizerem sentido</span>
          </div>
          <div className="doc-proposed-list">
            {proposed.map((d) => (
              <ProposedDocItem key={d.path} doc={d} onSave={() => saveProposed(d)} onDismiss={() => setProposed((p) => p.filter((x) => x.path !== d.path))} />
            ))}
          </div>
        </Card>
      )}

      {loading ? (
        <div className="center">Carregando…</div>
      ) : isEmpty ? (
        <EmptyState
          title="Base vazia"
          description="Crie pastas/arquivos, importe .md, ou peça a um agente para escrever um documento."
          action={<Button variant="primary" onClick={newFolder}>+ Criar primeira pasta</Button>}
        />
      ) : (
        <div className="docs-layout">
          <aside className="docs-tree">
            <input
              placeholder="🔎 buscar arquivo…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: '100%', padding: '6px 8px', marginBottom: 6, borderRadius: 4, border: '1px solid var(--border, #333)', background: 'var(--bg, #111)', color: 'inherit', fontSize: 13 }}
            />
            {filteredFiles ? (
              filteredFiles.length === 0 ? (
                <div style={{ padding: 8, color: 'var(--muted)', fontSize: 13 }}>nenhum arquivo encontrado</div>
              ) : (
                filteredFiles.map((f) => (
                  <div key={f.path} className={`docs-tree-row ${selectedFile === f.path ? 'is-active' : ''}`}>
                    <button type="button" className="docs-tree-label" title={f.path} onClick={() => openFile(f.path)}>
                      {iconFor(f.name)} {f.path.replace(/^docs\//, '')}
                    </button>
                  </div>
                ))
              )
            ) : (
              <>
                {!sourcesMode && (
                  <div className={`docs-tree-row ${selectedDir === 'docs' && !selectedFile ? 'is-active' : ''}`}>
                    <button type="button" className="docs-tree-label" onClick={() => { setSelectedDir('docs'); setSelectedFile(null); }}>
                      📁 docs
                    </button>
                    <span className="docs-tree-actions">
                      <button title="Gerar índice (IA)" onClick={() => generateIndex('docs')} disabled={busy}>✨</button>
                    </span>
                  </div>
                )}
                <TreeView
                  nodes={tree}
                  depth={sourcesMode ? 0 : 1}
                  expanded={expanded}
                  selectedFile={selectedFile}
                  selectedDir={selectedDir}
                  onToggle={(p) => setExpanded((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; })}
                  onSelectDir={(p) => { setSelectedDir(p); setSelectedFile(null); }}
                  onOpenFile={openFile}
                  onGenIndex={generateIndex}
                  onDelete={removeSelected}
                  busy={busy}
                />
              </>
            )}
          </aside>

          <Card className="docs-content">
            {selectedFile ? (
              <>
                <div className="docs-editor-head">
                  <div className="docs-editor-path">{selectedFile.replace(/^docs\//, '').split('/').join(' › ')}</div>
                  <div className="docs-editor-tools">
                    {slug && (
                      <a className="docs-open-tab" href={api.docViewUrl(slug, selectedFile)} target="_blank" rel="noopener noreferrer" title="Abrir a página sozinha em nova aba">abrir em nova aba ↗</a>
                    )}
                    <Button size="sm" variant="ghost" onClick={openHistory} disabled={histBusy}>{histBusy ? '…' : '🕘 histórico'}</Button>
                    <Button size="sm" variant="ghost" onClick={renameDoc} disabled={busy}>renomear/mover</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeSelected(selectedFile, false)}>apagar</Button>
                  </div>
                </div>
                {viewingSha && (
                  <div style={{ background: 'var(--surface-2, #23262e)', padding: '6px 10px', borderRadius: 6, marginBottom: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    👁️ Vendo versão <code>{viewingSha.slice(0, 8)}</code> (somente leitura).
                    <Button size="sm" variant="ghost" onClick={backToCurrent}>voltar ao atual</Button>
                    <Button size="sm" variant="primary" onClick={() => revertVersion(viewingSha)} disabled={busy}>reverter para esta</Button>
                  </div>
                )}
                {history && (
                  <div style={{ border: '1px solid var(--border, #333)', borderRadius: 6, marginBottom: 8, maxHeight: 240, overflow: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid var(--border, #333)' }}>
                      <strong style={{ fontSize: 13 }}>Histórico de versões</strong>
                      <Button size="sm" variant="ghost" onClick={() => setHistory(null)}>fechar</Button>
                    </div>
                    {history.length === 0 ? (
                      <p style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 10px', margin: 0 }}>Sem versões anteriores deste arquivo.</p>
                    ) : (
                      history.map((v) => (
                        <div key={v.sha} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border, #2a2a2a)', background: viewingSha === v.sha ? 'var(--surface-2, #23262e)' : undefined }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.message || '(sem mensagem)'}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{v.author} · {new Date(v.date).toLocaleString()} · <code>{v.sha.slice(0, 8)}</code></div>
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <Button size="sm" variant="ghost" onClick={() => viewVersion(v.sha)}>ver</Button>
                            <Button size="sm" variant="ghost" onClick={() => revertVersion(v.sha)} disabled={busy}>reverter</Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {/\.html?$/i.test(selectedFile) ? (
                  // HTML renderizado em iframe SANDBOXED (sem scripts) — seguro contra XSS.
                  <iframe className="doc-html-frame" sandbox="" srcDoc={content} title={selectedFile} />
                ) : viewingSha ? (
                  // Versão antiga: só leitura (use "reverter para esta" pra restaurar).
                  <div className="doc-md-wrap"><Markdown text={content} /></div>
                ) : (
                  <>
                    <MarkdownEditor
                      value={content}
                      onChange={setContent}
                      onSaveBlock={saveContent}
                      onSelect={(s) => setDocSel(s.text.trim() ? s : null)}
                      rightSlot={<Button size="sm" variant="primary" onClick={() => saveContent(content)}>Salvar</Button>}
                    />
                    {editDiff && (
                      <div style={{ marginTop: 10, border: '1px solid var(--border, #333)', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid var(--border, #333)' }}>
                          <strong style={{ fontSize: 13 }}>Mudanças da IA (diff)</strong>
                          <Button size="sm" variant="ghost" onClick={() => setEditDiff(null)}>fechar</Button>
                        </div>
                        <div style={{ maxHeight: 320, overflow: 'auto' }}><DiffView diff={editDiff} outputFormat="line-by-line" /></div>
                      </div>
                    )}
                    {slug && projectId && (
                      <DocChat
                        slug={slug}
                        projectId={projectId}
                        docPath={selectedFile}
                        onAfterTurn={onAfterTurn}
                        selection={docSel?.text ?? null}
                        onClearSelection={() => setDocSel(null)}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="docs-folder-hint">
                <p>Pasta selecionada: <strong>{selectedDir}</strong></p>
                <p style={{ color: 'var(--muted)', fontSize: 13 }}>
                  Selecione um arquivo na árvore para editar, ou use os botões acima para criar/importar.
                  O botão ✨ em cada pasta gera o <code>index.md</code> — o mapa que os agentes leem primeiro.
                </p>
              </div>
            )}
          </Card>
        </div>
      )}
      {promptModal && <PromptModal open {...promptModal} onClose={() => setPromptModal(null)} />}
    </Page>
  );
}

function TreeView({
  nodes, depth, expanded, selectedFile, selectedDir, onToggle, onSelectDir, onOpenFile, onGenIndex, onDelete, busy,
}: {
  nodes: DocTreeNode[]; depth: number; expanded: Set<string>; selectedFile: string | null; selectedDir: string;
  onToggle: (p: string) => void; onSelectDir: (p: string) => void; onOpenFile: (p: string) => void;
  onGenIndex: (p: string) => void; onDelete: (p: string, isDir: boolean) => void; busy: boolean;
}) {
  return (
    <>
      {nodes.map((n) => {
        const pad = { paddingLeft: depth * 14 } as const;
        if (n.type === 'dir') {
          const open = expanded.has(n.path);
          return (
            <Fragment key={n.path}>
              <div className={`docs-tree-row ${selectedDir === n.path && !selectedFile ? 'is-active' : ''}`} style={pad}>
                <button type="button" className="docs-tree-label" onClick={() => { onToggle(n.path); onSelectDir(n.path); }}>
                  {open ? '📂' : '📁'} {n.name}
                </button>
                <span className="docs-tree-actions">
                  <button title="Gerar índice (IA)" onClick={() => onGenIndex(n.path)} disabled={busy}>✨</button>
                  <button title="Apagar pasta" onClick={() => onDelete(n.path, true)}>×</button>
                </span>
              </div>
              {open && n.children && (
                <TreeView
                  nodes={n.children} depth={depth + 1} expanded={expanded} selectedFile={selectedFile} selectedDir={selectedDir}
                  onToggle={onToggle} onSelectDir={onSelectDir} onOpenFile={onOpenFile} onGenIndex={onGenIndex} onDelete={onDelete} busy={busy}
                />
              )}
            </Fragment>
          );
        }
        const isIndex = n.name === 'index.md';
        return (
          <div key={n.path} className={`docs-tree-row ${selectedFile === n.path ? 'is-active' : ''}`} style={pad}>
            <button type="button" className="docs-tree-label" onClick={() => onOpenFile(n.path)}>
              {isIndex ? '🗂️' : '📄'} {n.name}
            </button>
            <span className="docs-tree-actions">
              <button title="Apagar" onClick={() => onDelete(n.path, false)}>×</button>
            </span>
          </div>
        );
      })}
    </>
  );
}

function ProposedDocItem({ doc, onSave, onDismiss }: { doc: ProposedDoc; onSave: () => void | Promise<void>; onDismiss: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="doc-proposed-item">
      <div className="doc-proposed-head">
        <code>{doc.path}</code>
        <div className="doc-proposed-actions">
          <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)}>{open ? 'ocultar' : 'ver'}</Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>descartar</Button>
          <Button size="sm" variant="primary" onClick={save} disabled={saving}>{saving ? 'salvando…' : '💾 salvar'}</Button>
        </div>
      </div>
      {open && <div className="doc-proposed-preview"><Markdown text={doc.content} /></div>}
    </div>
  );
}

function parentDir(path: string): string {
  const i = path.lastIndexOf('/');
  return i > 0 ? path.slice(0, i) : 'docs';
}
